import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faClose} from '@fortawesome/free-solid-svg-icons/faClose';
import React, {ReactNode, useRef, useState} from 'react';
import {OFFER_TYPE, OFFER_TYPE_NICE_NAME, OfferModel} from '../../models/offer.model';
import {getCashBackWording} from '../../services/offer.service';

const Offer = ({offer, handleOfferSelected}: {offer: OfferModel, handleOfferSelected: (id: string) => void}) => {
	const {
		car_title,
		img,
		category,
		lease_amount_per_month,
		lease_months,
		lease_down_payment,
		lease_due_at_signing,
		terms,
		finance_apr,
		finance_months,
		custom_text,
		cash_amount,
		off_amount,
	} = offer;

	const [showTerms, setShowTerms] = useState(false);
	const [hasClickListener, setHasClickListener] = useState(false);
	const termsRef = useRef<HTMLDivElement>(null);

	const handleCloseTerms = (event: MouseEvent): void => {
		if (!termsRef.current?.contains(event.target as Node)) {
			setShowTerms(false);
		}
	};

	const leaseDownPaymentDetails = (lease_down_payment?: string, lease_due_at_signing?: string): ReactNode => {
		if (lease_down_payment) {
			return <div>${lease_down_payment} down payment</div>;
		} else if (lease_due_at_signing) {
			return <div>${lease_due_at_signing} due at signing</div>;
		} else {
			return '';
		}
	};

	const cashBackDetails = (offer): ReactNode => {
		const cashBackWording = getCashBackWording(offer);
		return <div>${cash_amount} {cashBackWording}</div>;
	};

	const handleOfferSelection = (offer: OfferModel): void => {
		setShowTerms(false);
		handleOfferSelected(offer.id);
	};

	const toggleTerms = (event: React.MouseEvent<HTMLButtonElement | HTMLDivElement>): void => {
		setShowTerms(!showTerms);
		event.stopPropagation();
		if (!showTerms) {
			if (!hasClickListener) {
				document.addEventListener('click', handleCloseTerms, {capture: true});
				setHasClickListener(true);
			}
		} else {
			document.removeEventListener('click', handleCloseTerms, {capture: true});
			setHasClickListener(false);
		}
	};

	return (
		<div className="flex justify-center">
			<div className="relative w-[220px] flex flex-col p-4 border rounded-md">
				<img src={img} alt="offer image"/>

				<div>
					<div className="text-xs pt-[8px]">{OFFER_TYPE_NICE_NAME[category]}</div>
					<div className="font-bold text-lg capitalize leading-tight">{car_title}</div>

					{category === OFFER_TYPE.LEASE && (
						<div className="text-sm">
							{lease_amount_per_month && lease_months && (
								<div>${lease_amount_per_month} mo. | {lease_months} mos.</div>
							)}
							{leaseDownPaymentDetails(lease_down_payment, lease_due_at_signing)}
						</div>
					)}

					{category === OFFER_TYPE.FINANCE && (
						<div className="text-sm">
							{finance_apr && finance_months && (
								<div>{finance_apr}% APR for {finance_months} mos.</div>
							)}
							{custom_text && <div>{custom_text}</div>}
						</div>
					)}

					{category === OFFER_TYPE.CASH && cash_amount && (
						<div className="text-sm">
							{cashBackDetails(offer)}
						</div>
					)}

					{category === OFFER_TYPE.OFF_MSRP && off_amount && (
						<div className="text-sm">
							<div>${off_amount} Off MSRP</div>
						</div>
					)}

					{terms && (
						<div className="relative mt-[4px] w-full">
							<button type="button" className="text-xs cursor-pointer text-gray-500" onClick={toggleTerms}>
								Terms & Conditions
							</button>
							{showTerms &&
								<div ref={termsRef}
									className="absolute left-[-10] top-full mt-[-16px] w-[212px] max-h-[min(50vh,240px)] overflow-y-auto bg-white text-xs rounded-md border border-primary p-2 text-gray-700 shadow-lg">
									<div onClick={toggleTerms}
										className="absolute right-[0px] top-[0px] py-[6px] px-[12px] text-lg cursor-pointer">
										<FontAwesomeIcon icon={faClose}/>
									</div>
									<div className="font-medium mb-[12px]">Terms & Conditions</div>
									{terms}
								</div>
							}
						</div>
					)}
				</div>

				<button	className="self-center border border-primary bg-primary rounded-full text-white font-bold mt-[20px] py-2 px-4 hover:bg-white hover:text-primary cursor-pointer"
						onClick={() => handleOfferSelection(offer)}>
					Claim Offer
				</button>

			</div>
		</div>
	);
};

export default Offer;
